import {PersistentCityModel} from "@/models/PersistentCityModel";
import {toTimestamp} from "@/dateUtils";
import {getDocs, where, query, collectionGroup, collection} from "firebase/firestore";
import {Address} from "@/models/Address";
import Timer from "@/models/Timer";
import {
  haversineDistance,
  calculateBoundingBox,
  feetToMiles,
  isPointInBounds
} from "@strenforcement/domain/geo";

import { STREET_SUFFIX_REGEX } from "@strenforcement/domain/address";
import {
  normalizePropertyUrl,
  computePropertyImageUrls,
  getPropertyPrimaryImageUrl,
  computePropertyAddressLines
} from "@strenforcement/domain/property";
import { ViolationStatus, ViolationReason } from "@strenforcement/domain/violation";
import {normalizeAddressLines} from "@/addressFormatting";

export { ViolationStatus, ViolationReason };

export class Property extends PersistentCityModel {

  static collectionName = 'properties';

  static async findNearby(city, {lat, lng, radius, limit = 25}) {
    let timer = new Timer();

    // Calculate bounding box from center point and radius (in feet)
    const bounds = calculateBoundingBox({ lat, lng, radiusFeet: radius });

    const snapshot = await getDocs(
      query(
        city.subCollection('properties'),
        where('latlng.lat', '>=', bounds.minLat),
        where('latlng.lat', '<=', bounds.maxLat)
      )
    );
    timer.log(`Found ${snapshot.docs.length} properties within latitude range`);

    // Manually filter by longitude (Firestore only allows range filters on one field)
    let filtered = snapshot.docs.filter(doc => {
      const latlng = doc.data().latlng;
      return isPointInBounds(latlng, bounds);
    });
    timer.log(`Found ${filtered.length} properties within latitude/longitude range`);

    // Filter by actual distance (bounding box is an approximation)
    const radiusMiles = feetToMiles(radius);
    const center = { lat, lng };
    filtered = filtered.filter(doc => {
      const latlng = doc.data().latlng;
      const d = haversineDistance(center, latlng);
      return d !== null && d < radiusMiles;
    });
    timer.log(`Found ${filtered.length} properties within radius`);

    // Sort by distance and limit results
    return filtered
      .map(doc => ({ doc, dist: haversineDistance(center, doc.data().latlng) }))
      .sort((a, b) => a.dist - b.dist)
      .slice(0, limit)
      .map(({ doc }) => new Property(city, doc));
  }

  static useCollectionGroups = false;
  static async search(city, terms, field) {
    terms = terms.trim().toLowerCase().replace(STREET_SUFFIX_REGEX, '').trim();
    // let terms2 = terms.substr(terms.indexOf(' ')).trim();
    let propertyIds = [];
    if (this.useCollectionGroups) {
      let queryArgs = [
        collectionGroup(city.db, 'matches'),
        where('matchValue', '>=', terms),
        where('matchValue', '<', terms+'~'),
      ];
      if (field) {
        queryArgs.push(where('field', '==', field));
      }
      const q = query(...queryArgs);

      const snapshot = await getDocs(q);


      propertyIds = snapshot.docs
        .filter(doc => {
          return doc.ref.path.startsWith(`/cities/${city.id}/properties_index/`);
        })
        .map(doc => doc.id);
    } else {
      const q = query(
        city.subCollection('properties_index'),
        where('matchValue', '>=', terms),
        where('matchValue', '<', terms+'~')
      );

      console.log(`Searching properties_index`);
      const snapshot = await getDocs(q);


      const snapshots = await Promise.all(
        snapshot.docs
        .map(doc => {
          return getDocs(collection(doc.ref, 'matches'));
        })
      );

      propertyIds = [];
      snapshots.forEach(snap => {
        snap.docs.forEach(doc => {
          if (!field || field === doc.data().field) {
            propertyIds.push(doc.id);
          }
        });
      });
    }


    if (propertyIds.length === 0) {
      return [];
    }

    let batches = [];
    propertyIds.forEach(id => {
      let batch = batches[batches.length - 1];
      if (!batch || batch.length >= 30) {
        batch = [];
        batches.push(batch);
      }
      batch.push(id);
    });

    const propertyBatches = await Promise.all(batches.map(async batch => {
      return await this.snapshotConverter(city)(await getDocs(
        query(
          city.subCollection('properties'),
          where('id', 'in', batch)
        )
      ));
    }));

    let properties = [];
    propertyBatches.forEach(batch => properties.push(...batch));

    return properties;
  }

  constructor(city, doc) {
    super(city, doc);
  }

  get idFieldNameForApi() {
    return 'propertyId';
  }

  get url() {
    const url = this.details.url || this.data.url;
    if (url) {
      return normalizePropertyUrl(url);
    } else {
      return this.city.recordsUrl;
    }
  }

  get address() {
    const address = this.data.address;
    return address ? new Address(address) : null;
  }

  get addressFormatted() {
    const address = this.address;
    if (address) {
      return address.formatStreet(true);
    } else {
      return this.details.address || '';
    }
  }

  get addressLines() {
    return computePropertyAddressLines({
      addressFormatted: this.addressFormatted,
      place: this.data.googlePlace,
      city: this.city
    });
  }

  get dateUpdated() {
    return toTimestamp(this.data.dateUpdated);
  }

  get latlng() {
    return this.data.latlng;
  }

  get latLngFormatted() {
    if (this.data.latlng) {
      return `${this.data.latlng.lat}, ${this.data.latlng.lng}`;
    } else {
      return null;
    }
  }

  distanceFrom(/* { lat, lng } */ point) {
    return haversineDistance(point, this.latlng);
  }

  get details() {
    return {
      stats: {},
      imageUrls: [],
      ...this.data.details
    };
  }

  get owners() {
    return [
      ...(this.data.owners || []),
      ...(this.details.owners || [])
    ];
  }

  get ownerAddress() {
    return this.details.ownerAddress || this.data.ownerAddress || this.addressFormatted;
  }

  get ownerAddressLines() {
    return normalizeAddressLines(this.ownerAddress);
  }

  get realtorDetails() {
    return this.data.realtorDetails || {};
  }

  get firstAssessorImageUrl() {
    return (this.details.imageUrls || [])[0] || (this.data.imageUrls || [])[0];
  }

  get firstRealtorImageUrl() {
    return null;
    // return (this.realtorDetails.imageUrls || [])[0];
  }

  get realtorUrl() {
    return this.realtorDetails.url;
  }

  get imageUrls() {
    return computePropertyImageUrls({
      realtorDetails: this.realtorDetails,
      details: this.details,
      data: this.data
    });
  }

  get imageUrl() {
    return getPropertyPrimaryImageUrl(this.data);
  }

  get thumbnailUrl() {
    return this.data.thumbnailUrl || null;
  }

  get imageCount() {
    let count = 0;
    count += (this.details.imagesUrls || []).length;
    count += (this.realtorDetails.imageUrls || []).length;
    return count;
  }

  get detailsImported() {
    return this.data.recordDetailsImported || !!this.data.details;
  }

  get hasDetails() {
    return !!this.data.details;
  }

  get dateDetailsImported() {
    return toTimestamp(this.data.dateRecordDetailsImported);
  }

  get detailsUrl() {
    return this.url;
  }

  importDetails() {
    return this.doApiCall('importPropertyDetails');
  }

  get isImportingDetails() {
    return this.isRequesting('importPropertyDetails');
  }

  get latLngImported() {
    return this.data.latLngImported || !!this.data.latlng;
  }

  get hasLatLng() {
    return !!this.data.latlng;
  }

  get dateLatLngImported() {
    return toTimestamp(this.data.dateLatLngImported);
  }

  get latLngUrl() {
    const address = this.addressFormatted + ' ' + this.city.label;
    return `https://www.google.com/maps/place/${encodeURIComponent(address)}`;
  }

  importLatLng() {
    return this.doApiCall('importPropertyLatLng');
  }

  get addressId() {
    return (this.data.googlePlace || {}).id;
  }

  get realtorDetailsImported() {
    return this.data.realtorDetailsImported || !!this.data.realtorDetails;
  }

  get hasRealtorDetails() {
    return !!this.data.realtorDetails;
  }

  get dateRealtorDetailsImported() {
    return toTimestamp(this.data.dateRealtorDetailsImported);
  }

  indexKeys() {
    const address = this.addressFormatted.toLowerCase();
    const host = (this.owners.join(' ')).toLowerCase();
    return (address + ' ' + host + ' ' + this.id)
    .split(/ /g)
    .map(word => word.trim())
    .filter(word => !!word);
  }

  //
  // Violations
  //

  get violations() {
    return this.data.violations || {};
  }

  get isViolationSeen() {
    return this.violations.seen || false;
  }

  get violationReason() {
    const violations = this.violations;
    if (violations?.status === 'violation') {
      if (violations.licenseStatus === 'none' || !violations.licenseStatus) {
        return {
          type: ViolationReason.noLicense,
          licenseStatus: null
        };
      } else if (violations.licenseStatus === 'expired') {
        return {
          type: ViolationReason.expiredLicense,
          licenseStatus: 'expired'
        };
      } else {
        return {
          type: ViolationReason.inactiveLicense,
          licenseStatus: violations.licenseWorkflowStatus
        };
      }
    } else {
      return null;
    }
  }

  /**
   * TODO Make this per-user, instead of one per-document seen flag
   * @param {boolean} seen
   * @return {Promise<Property>}
   */
  async updateViolationSeen(seen) {
    return await this.update({
      violations: {
        seen: seen || false
      }
    }, null, false);
  }

  get isViolation() {
    return this.violations.status === ViolationStatus.violation;
  }

  get violationsEvaluatedAt() {
    const violations = this.violations;
    return violations?.lastEvaluatedAt ?? violations?.detectedAt ?? null;
  }
}
